import { Box, Button, Stack } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import DocumentTextField from "../../../../components/DocumentTextField";
import HidenTextField from "../../../../components/HidenTextField";
import PdfModal from "../../../../components/PdfModal";
import RegisterPasswordTextField from "../../../../components/RegisterPasswordTextField";
import useFormValidity from "../stepUtils";
import "../style.css";

export interface AddressStepProps {
  handleBack: () => void;
  handleCancel: () => void;
}

function AuthInfoStep({
  handleBack,
  handleCancel,
}: AddressStepProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [openTerm, setOpenTerm] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>("/assets/pdfs/termo_de_uso_smartdocs_SESI_DN.pdf");
  const [isFocusConfirmPassword, setIsFocusConfirmPassword] =
    useState<boolean>(false);
  const { control } = useFormContext();
  const isFormsValid = useFormValidity([
    "password",
    "confirmation_password",
    "term_accepted",
  ]);

  useEffect(() => {
    const fetchTermUrl = async () => {
      const currentLang = i18n.language;
      const baseUrl = "/assets/pdfs/termo_de_uso_smartdocs_SESI_DN";
      
      if (currentLang && currentLang !== "pt-BR") {
        const langUrl = `${baseUrl}_${currentLang}.pdf`;
        try {
          const response = await fetch(langUrl, { method: "HEAD" });
          if (response.ok) {
            setPdfUrl(langUrl);
            return;
          }
        } catch (error) {
          console.error("Error fetching translated term:", error);
        }
      }
      setPdfUrl(`${baseUrl}.pdf`);
    };

    fetchTermUrl();
  }, [i18n.language]);

  const handleOpenTerm = (): void => {
    setOpenTerm(true);
  };

  const handleCloseTerm = (): void => {
    setOpenTerm(false);
  };

  return (
    <Stack alignItems="center" marginBottom="5%" spacing={3}>
      <Controller
        control={control}
        name="cpf"
        render={({ field, fieldState: { error } }) => (
          <DocumentTextField
            {...field}
            disabled
            error={Boolean(error)}
            fullWidth
            helperText={error?.message || " "}
            variant="outlined"
          />
        )}
      />
      <Controller
        control={control}
        name="password"
        render={({ field, fieldState: { error } }) => {
          const matches = (() => {
            const result =
              error && error.types ? (error.types.matches as string[]) : [];
            if (!Array.isArray(result)) return [result];
            return result;
          })();

          return (
            <RegisterPasswordTextField
              {...field}
              digitsError={matches.some((item) => item === "digitsError")}
              error={Boolean(error)}
              fullWidth
              label={t("resetPassword.passwordLabel")}
              lowercaseLetterError={matches.some(
                (item) => item === "lowercaseLetterError"
              )}
              minMaxError={matches.some((item) => item === "minMaxError")}
              slotProps={{ htmlInput: { maxLength: 16 } }}
              specialCharError={matches.some(
                (item) => item === "specialCharError"
              )}
              uppercaseLetterError={matches.some(
                (item) => item === "uppercaseLetterError"
              )}
              variant="outlined"
            />
          );
        }}
      />
      <Controller
        control={control}
        name="confirmation_password"
        render={({ field, fieldState: { error } }) => {
          return (
            <HidenTextField
              {...field}
              error={Boolean(error)}
              fullWidth
              helperText={
                isFocusConfirmPassword && !field.value && !error?.message ? (
                  <Typography
                    color="common.black"
                    component="div"
                    variant="caption"
                  >
                    {t("register.confirmPasswordHelperText")}
                  </Typography>
                ) : (
                  error?.message || " "
                )
              }
              label={t("register.confirmPasswordLabel")}
              onBlur={() => {
                setIsFocusConfirmPassword(false);
                field.onBlur();
              }}
              onFocus={() => {
                setIsFocusConfirmPassword(true);
              }}
              slotProps={{ htmlInput: { maxLength: 16 } }}
            />
          );
        }}
      />
      <Controller
        control={control}
        name="term_accepted"
        render={({ field }) => (
          <div className="term-card">
            <FormControlLabel
              control={<Checkbox {...field} />}
              label={
                <Typography>
                  {t("register.acceptTerms")}{" "}
                  <Link onClick={handleOpenTerm}>{t("register.consentTerms")}</Link>
                </Typography>
              }
            />
          </div>
        )}
      />
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        marginTop="5%"
        width="100%"
      >
        <Button
          color="secondary"
          onClick={handleBack}
          size="large"
          tabIndex={-1}
          type="button"
          variant="outlined"
        >
          {t("common.previous")}
        </Button>
        <Button
          color="primary"
          disabled={!isFormsValid}
          size="large"
          tabIndex={0}
          type="submit"
          variant="contained"
        >
          {t("common.finish")}
        </Button>
      </Box>
      <Box marginTop="10%" textAlign="end" width="100%">
        <Button
          color="success"
          onClick={handleCancel}
          size="medium"
          variant="text"
        >
          {t("common.cancelRegistration")}
        </Button>
      </Box>
      <PdfModal handleClose={handleCloseTerm} open={openTerm} pdfUrl={pdfUrl} />
    </Stack>
  );
}

export default AuthInfoStep;
